import { EyeOff, PanelLeft } from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { MenuConfigApi } from '../../hooks/useMenuConfig';
import IconGlyph from './IconGlyph';

/**
 * Live sidebar preview — renders the configuration the way the real app
 * sidebar will: ivory paper, icon + label rows, sub-items indented, the first
 * visible item shown in its "active" state with a champagne left rail.
 * Hidden items are omitted (counted below); disabled items render struck.
 */
export default function SidebarPreview({ api, roleName }: { api: MenuConfigApi; roleName: string }) {
  const visibleItems = api.menuItems.filter(
    (item) => !(api.menuStates.get(item.id)?.hidden ?? false),
  );
  const hiddenCount = api.menuItems.length - visibleItems.length;
  const firstVisibleId = visibleItems[0]?.id;

  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      {/* Panel header */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
      >
        <PanelLeft size={14} style={{ color: SOVEREIGN.champagne }} />
        <div>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.12em]"
            style={{ color: SOVEREIGN.ink }}
          >
            Live preview
          </p>
          <p className="text-[10.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
            Sidebar as {roleName || 'this role'} will see it
          </p>
        </div>
      </div>

      {/* Faux sidebar */}
      <div className="max-h-[46vh] overflow-y-auto p-3" style={{ background: SOVEREIGN.paper }}>
        {visibleItems.length === 0 ? (
          <p className="py-6 text-center text-[11.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
            Nothing visible — every item is hidden.
          </p>
        ) : (
          <nav className="space-y-0.5" aria-label="Sidebar preview">
            {visibleItems.map((item) => {
              const st = api.menuStates.get(item.id) || { enabled: true, hidden: false };
              const icon = st.icon || item.icon;
              const isActiveSample = item.id === firstVisibleId;
              const visibleSubs =
                item.subItems?.filter(
                  (sub) => !(api.subMenuStates.get(item.id)?.get(sub.id)?.hidden ?? false),
                ) ?? [];

              return (
                <div key={item.id}>
                  <div
                    className="relative flex items-center gap-2.5 rounded-lg px-2.5 py-2"
                    style={
                      isActiveSample
                        ? { background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.barTrackBorder}` }
                        : { border: '1px solid transparent' }
                    }
                  >
                    {isActiveSample && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full"
                        style={{ background: SOVEREIGN.champagne }}
                      />
                    )}
                    <IconGlyph
                      icon={icon}
                      className="w-4 h-4"
                      color={
                        !st.enabled
                          ? SOVEREIGN.kpiCaption
                          : isActiveSample
                            ? SOVEREIGN.bottle
                            : SOVEREIGN.kpiCaption
                      }
                    />
                    <span
                      className="min-w-0 flex-1 truncate text-[12px]"
                      style={{
                        color: !st.enabled
                          ? SOVEREIGN.kpiCaption
                          : isActiveSample
                            ? SOVEREIGN.title
                            : SOVEREIGN.ink,
                        fontWeight: isActiveSample ? 700 : 500,
                        textDecoration: st.enabled ? 'none' : 'line-through',
                      }}
                    >
                      {item.name}
                    </span>
                  </div>

                  {visibleSubs.length > 0 && (
                    <div
                      className="ml-[1.35rem] space-y-0.5 border-l pl-2.5"
                      style={{ borderColor: SOVEREIGN.innerRule }}
                    >
                      {visibleSubs.map((sub) => {
                        const subSt = api.subMenuStates.get(item.id)?.get(sub.id) || {
                          enabled: true,
                          hidden: false,
                        };
                        return (
                          <div key={sub.id} className="flex items-center gap-2 px-1.5 py-1">
                            <span
                              className="truncate text-[11px]"
                              style={{
                                color: subSt.enabled ? SOVEREIGN.ink : SOVEREIGN.kpiCaption,
                                textDecoration: subSt.enabled ? 'none' : 'line-through',
                              }}
                            >
                              {sub.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}
      </div>

      {hiddenCount > 0 && (
        <div
          className="flex items-center gap-1.5 px-4 py-2 text-[10.5px]"
          style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}`, color: SOVEREIGN.kpiCaption }}
        >
          <EyeOff size={11} />
          {hiddenCount} hidden item{hiddenCount === 1 ? '' : 's'} not shown
        </div>
      )}
    </div>
  );
}
